import {useEffect, useRef} from 'react'

type WakeLockSentinelLike = {
  released: boolean
  release: () => Promise<void>
  addEventListener: (type: 'release', listener: () => void) => void
}

type NavigatorWithWakeLock = Navigator & {
  wakeLock?: {request: (type: 'screen') => Promise<WakeLockSentinelLike>}
}

export const useKeepAwake = (enabled: boolean): void => {
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const oscillatorRef = useRef<OscillatorNode | null>(null)

  useEffect(() => {
    if (!enabled) return

    let cancelled = false

    const requestWakeLock = async () => {
      const nav = navigator as NavigatorWithWakeLock
      if (!nav.wakeLock || document.visibilityState !== 'visible') return
      if (wakeLockRef.current && !wakeLockRef.current.released) return

      try {
        const sentinel = await nav.wakeLock.request('screen')
        if (cancelled) {
          await sentinel.release()
          return
        }
        wakeLockRef.current = sentinel
        sentinel.addEventListener('release', () => {
          wakeLockRef.current = null
        })
      } catch {
        wakeLockRef.current = null
      }
    }

    const startSilentAudio = () => {
      const context = audioContextRef.current
      if (context) {
        if (context.state === 'suspended') {
          context.resume().catch(() => {})
        }
        return
      }

      const AudioContextClass =
        window.AudioContext || (window as unknown as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext
      if (!AudioContextClass) return

      try {
        const ctx = new AudioContextClass()
        const oscillator = ctx.createOscillator()
        const gain = ctx.createGain()
        gain.gain.value = 0
        oscillator.connect(gain)
        gain.connect(ctx.destination)
        oscillator.start()
        audioContextRef.current = ctx
        oscillatorRef.current = oscillator
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {})
        }
      } catch {
        audioContextRef.current = null
        oscillatorRef.current = null
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      requestWakeLock()
      startSilentAudio()
    }

    const handleUserGesture = () => {
      startSilentAudio()
    }

    requestWakeLock()
    startSilentAudio()

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pointerdown', handleUserGesture)
    window.addEventListener('keydown', handleUserGesture)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pointerdown', handleUserGesture)
      window.removeEventListener('keydown', handleUserGesture)

      const sentinel = wakeLockRef.current
      if (sentinel && !sentinel.released) {
        sentinel.release().catch(() => {})
      }
      wakeLockRef.current = null

      const oscillator = oscillatorRef.current
      if (oscillator) {
        try {
          oscillator.stop()
        } catch {
          oscillatorRef.current = null
        }
      }
      oscillatorRef.current = null

      const context = audioContextRef.current
      if (context && context.state !== 'closed') {
        context.close().catch(() => {})
      }
      audioContextRef.current = null
    }
  }, [enabled])
}

export default useKeepAwake
